// Web-portal bridge (CrazyGames HTML5 SDK v2). Flat module, repo style:
// every call is a safe no-op unless BOTH are true — the build was made with
// VITE_PORTAL set (npm run build:portal) AND the SDK script tag (injected by
// vite.config.ts in portal mode) actually loaded. The game must never break
// because the SDK is missing, blocked, or errors.
//
// Basic Launch scope: loading + gameplay events only (CrazyGames uses them
// for conversion/playtime measurement). Ads land in a later step once the
// game is invited to Full Launch.

/** Minimal surface of the v2 SDK we call. */
interface CrazySdk {
  game?: {
    sdkGameLoadingStart?: () => void;
    sdkGameLoadingStop?: () => void;
    gameplayStart?: () => void;
    gameplayStop?: () => void;
  };
}

declare global {
  interface Window { CrazyGames?: { SDK?: CrazySdk }; }
}

export function isPortalBuild(): boolean {
  return !!import.meta.env.VITE_PORTAL;
}

function sdkGame(): NonNullable<CrazySdk["game"]> | null {
  if (!isPortalBuild()) return null;
  return window.CrazyGames?.SDK?.game ?? null;
}

let loadingReported = false;
let loadingStopped = false;
let gameplayActive = false;

export const portal = {
  /** BootScene.preload — mark the start of the initial asset download. */
  loadingStart(): void {
    if (loadingReported) return;
    loadingReported = true;
    try { sdkGame()?.sdkGameLoadingStart?.(); } catch { /* never break boot */ }
  },

  /** First MenuScene — the initial download is over. Once per session. */
  loadingStop(): void {
    if (loadingStopped || !loadingReported) return;
    loadingStopped = true;
    try { sdkGame()?.sdkGameLoadingStop?.(); } catch { /* ignore */ }
  },

  /** Player is actively playing (first wave started / run resumed). Deduped. */
  gameplayStart(): void {
    if (gameplayActive) return;
    gameplayActive = true;
    try { sdkGame()?.gameplayStart?.(); } catch { /* ignore */ }
  },

  /** Run ended (game over / quit to menu). Deduped. */
  gameplayStop(): void {
    if (!gameplayActive) return;
    gameplayActive = false;
    try { sdkGame()?.gameplayStop?.(); } catch { /* ignore */ }
  },
};
